import { useQuery } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { AccountHead, AccountSection, goLogin, useAccountUi, ZoneSelect, type Zone } from "../../account/accountUi";
import { postJson, readView, useDocumentTitle, useHeadingFocus, useSpaLinks } from "../../config/common";
import { DraftGuard } from "../../config/team";
import { text, type TeamUi } from "../../team/teamText";
import { useFeedback } from "../../team/TeamUi";
import { ChatIcon } from "../../ui/icons/Icon";
import { TextField } from "../../users/formParts";
import { UsersError, UsersFrame } from "../../users/usersUi";

// Ajustes de la empresa (/portal/configuracion/empresa, Configuracion/Empresa.razor) con el kit. Dueño y administrador
// editan ahí mismo el nombre comercial y la zona horaria (sin el paso «Editar» aparte); los datos de registro (código,
// razón social, estado, idioma y moneda) se muestran como datos. Quien no puede editar ve todo como solo lectura C41.

type View = {
  canEdit: boolean;
  code: string;
  legalName: string;
  commercialName: string | null;
  active: boolean;
  timezone: string;
  timezoneLabel: string;
  language: string | null;
  currency: string | null;
  zones: Zone[] | null;
};
type Field = "commercialName" | "timezone";
type Reply = { saved?: boolean; errorKey?: string; fields?: Partial<Record<Field, string>>; forbidden?: boolean };

const languageKey: Record<string, string> = { "ES-PE": "LANG_ES_PE", "EN-US": "LANG_EN_US" };
const currencies = ["PEN", "USD", "EUR", "MXN", "CLP", "COP"];

export function CompanyPage({ locale }: { locale: string }) {
  const ui = useAccountUi(locale);
  const view = useQuery({
    queryKey: ["settings", "company"],
    queryFn: () => readView<View & { forbidden?: boolean }>("/api/portal/settings/company"),
    staleTime: 0,
  });
  useSpaLinks();
  useHeadingFocus(!!ui && !!view.data);
  useDocumentTitle(ui ? text(ui, "COMPANY_TITLE") : null);
  if (!ui) return null;
  if (view.data?.status === 401) {
    goLogin();
    return null;
  }
  const ready = !!view.data || view.isError;
  const data = view.data?.status === 200 ? view.data.body : null;
  const back = { href: "/portal/configuracion", labelKey: "SETTINGS_TITLE" };
  return (
    <UsersFrame ui={ui} ready={ready}>
      <AccountHead
        ui={ui}
        title={text(ui, "COMPANY_TITLE")}
        sub={data ? data.commercialName || data.legalName : undefined}
        back={back}
      />
      {data ? (
        data.canEdit ? (
          <CompanyForm key={view.dataUpdatedAt} ui={ui} view={data} reload={() => void view.refetch()} />
        ) : (
          <CompanyReadOnly ui={ui} view={data} />
        )
      ) : view.data?.status === 403 ? (
        <div className="tm-denied" role="status" data-part="sin-permiso">
          <strong>{text(ui, "TEAM_ERR_FORBIDDEN")}</strong>
          <a className="tm-btn secondary" href="/portal/configuracion">
            {text(ui, "SETTINGS_TITLE")}
          </a>
        </div>
      ) : (
        <UsersError
          message={text(ui, "TEAM_ERR_GENERIC")}
          retryLabel={text(ui, "CFG1_RETRY")}
          onRetry={() => void view.refetch()}
        />
      )}
    </UsersFrame>
  );
}

/** Idioma y moneda con su nombre; un código que no se conoce se muestra tal cual. */
const languageName = (ui: TeamUi, code: string | null) =>
  !code ? "—" : languageKey[code.trim().toUpperCase()] ? text(ui, languageKey[code.trim().toUpperCase()]!) : code;
const currencyName = (ui: TeamUi, code: string | null) =>
  !code ? "—" : currencies.includes(code.trim().toUpperCase()) ? text(ui, `CUR_${code.trim().toUpperCase()}`) : code;

/** Los datos de registro, que se ven igual para todos (C41: texto y etiqueta, sin controles). */
function RegistryData({ ui, view, withEditable }: { ui: TeamUi; view: View; withEditable: boolean }) {
  return (
    <dl className="tm-dl acc-dl" data-part="datos-registro">
      {withEditable ? (
        <>
          <dt>{text(ui, "COMPANY_COMMERCIAL_NAME")}</dt>
          <dd>{view.commercialName || "—"}</dd>
          <dt>{text(ui, "COMPANY_TZ")}</dt>
          <dd>{view.timezoneLabel}</dd>
        </>
      ) : null}
      <dt>{text(ui, "COMPANY_LEGAL_NAME")}</dt>
      <dd>{view.legalName}</dd>
      <dt>{text(ui, "COMPANY_CODE")}</dt>
      <dd>{view.code}</dd>
      <dt>{text(ui, "COMPANY_STATUS_LBL")}</dt>
      <dd>
        <span className={`tm-tag ${view.active ? "accent" : "ochre"}`}>
          {text(ui, view.active ? "INBOX_STATUS_ACTIVE" : "INBOX_STATUS_INACTIVE")}
        </span>
      </dd>
      <dt>{text(ui, "COMPANY_LANG")}</dt>
      <dd>{languageName(ui, view.language)}</dd>
      <dt>{text(ui, "COMPANY_CURR")}</dt>
      <dd>{currencyName(ui, view.currency)}</dd>
    </dl>
  );
}

function CompanyReadOnly({ ui, view }: { ui: TeamUi; view: View }) {
  return (
    <>
      <div className="tm-notice neutral acc-notice" role="status" data-part="solo-lectura">
        <ChatIcon name="lock-keyhole" />
        <span>{text(ui, "ACC_COMPANY_READONLY")}</span>
      </div>
      <RegistryData ui={ui} view={view} withEditable />
    </>
  );
}

function CompanyForm({ ui, view, reload }: { ui: TeamUi; view: View; reload: () => void }) {
  const T = (key: string) => text(ui, key);
  const feedback = useFeedback();
  // Lo guardado: el borrador se compara con esto (no con la lectura), así DraftGuard sigue bien aunque la relectura falle.
  const [baseline, setBaseline] = useState({ commercialName: view.commercialName ?? "", timezone: view.timezone });
  const [draft, setDraft] = useState(baseline);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const dirty = draft.commercialName.trim() !== baseline.commercialName || draft.timezone !== baseline.timezone;
  const focusField = (field: Field) =>
    requestAnimationFrame(() =>
      form.current?.querySelector<HTMLElement>(`[data-field="${field}"] :is(input,button)`)?.focus(),
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found: Partial<Record<Field, string>> = {};
    if (draft.commercialName.trim().length > 200) found.commercialName = T("COMPANY_ERROR_FIELDS");
    if (!draft.timezone) found.timezone = T("CHAT_R3_TIMEZONE_INVALID");
    setErrors(found);
    setError(null);
    const first = (["commercialName", "timezone"] as Field[]).find((field) => found[field]);
    if (first) return focusField(first);
    setBusy(true);
    try {
      const reply = await postJson<Reply>("/api/portal/settings/company", {
        commercialName: draft.commercialName.trim(),
        timezone: draft.timezone,
      });
      if (reply.status === 401) return goLogin();
      if (reply.status === 200 && reply.body?.saved) {
        // Un nombre vacío conserva el anterior en Platform.
        setBaseline({
          commercialName: draft.commercialName.trim() || baseline.commercialName,
          timezone: draft.timezone,
        });
        feedback.show(T("COMPANY_SAVED"));
        // Vuelve a leer: un nombre vacío conserva el anterior y la etiqueta de la zona sale del servidor.
        reload();
        return;
      }
      const fields = reply.body?.fields ?? {};
      const issues = Object.fromEntries(Object.entries(fields).map(([field, key]) => [field, T(key ?? "")]));
      if (Object.keys(issues).length > 0) {
        setErrors(issues);
        focusField(Object.keys(issues)[0] as Field);
      } else setError(T(reply.body?.errorKey ?? "COMPANY_ERROR_SAVE"));
    } catch {
      setError(T("COMPANY_ERROR_SAVE"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form ref={form} className="tm-form tm-user-form acc-form" noValidate onSubmit={(event) => void submit(event)}>
      <DraftGuard dirty={dirty} L={T} />
      {error ? <UsersError message={error} /> : null}
      <section className="tm-inbox-section" data-part="editable">
        <div className="tm-fields">
          <TextField
            id="company-commercial"
            field="commercialName"
            label={T("COMPANY_COMMERCIAL_NAME")}
            help={T("COMPANY_EMPTY_NAME_HINT")}
            value={draft.commercialName}
            error={errors.commercialName}
            maxLength={200}
            autoComplete="organization"
            onChange={(commercialName) => {
              setDraft({ ...draft, commercialName });
              setErrors((old) => ({ ...old, commercialName: undefined }));
            }}
          />
          <ZoneSelect
            ui={ui}
            id="company-zone"
            label={T("COMPANY_TZ")}
            value={draft.timezone}
            zones={view.zones ?? []}
            error={errors.timezone}
            help={T("ACC_COMPANY_TZ_HELP")}
            onChange={(timezone) => {
              setDraft({ ...draft, timezone });
              setErrors((old) => ({ ...old, timezone: undefined }));
            }}
          />
        </div>
      </section>
      <AccountSection
        id="company-registry-title"
        title={T("ACC_COMPANY_REGISTRY")}
        line={T("ACC_COMPANY_REGISTRY_LINE")}
        part="registro"
      >
        <RegistryData ui={ui} view={view} withEditable={false} />
      </AccountSection>
      <div className="tm-form-actions" data-fixed-actions="" data-part="acciones">
        <a className="tm-btn secondary" href="/portal/configuracion">
          {T("ACTION_CANCEL")}
        </a>
        <button type="submit" className="tm-btn primary" disabled={busy} aria-busy={busy ? "true" : undefined}>
          {T("ACTION_SAVE")}
        </button>
      </div>
    </form>
  );
}
